import { Store } from "@/models/storeModel";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Edit,
  Trash,
  Users,
  Utensils,
  Coffee,
  Wine,
  Hotel,
  Sparkles,
  Scissors,
  Heart,
  ShoppingBag,
  Dumbbell,
  Stethoscope,
  GraduationCap,
  MoreHorizontal,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";
import { EditStoreDialog } from "./EditStoreDialog";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { ConfirmDeleteDialog } from "@/components/tables/ConfirmDeleteDialog";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuthStore } from "@/store/authStore";
import { isAdminRole } from "@/utils/roleUtils";
import { GetIndustryIcon, getIndustryColor } from "@/utils/industryUtils";

interface StoreListProps {
  stores: Store[];
  isLoading: boolean;
  isAdminHQ: boolean;
  onSelectStore: (storeId: string) => void;
}

export function StoreList({
  stores,
  isLoading,
  isAdminHQ,
  onSelectStore,
}: StoreListProps) {
  const { deleteStore } = useStoreManagementStore();
  const { user } = useAuthStore();
  const [storeToEdit, setStoreToEdit] = useState<Store | null>(null);
  const [storeToDelete, setStoreToDelete] = useState<Store | null>(null);
  const [showUnauthorizedDialog, setShowUnauthorizedDialog] = useState(false);
  const { toast } = useToast();

  const formatIndustryType = (industryType: string) => {
    if (!industryType) return "Other";
    return industryType
      .replace("_", " ")
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleDelete = async () => {
    if (!storeToDelete) return;

    try {
      await deleteStore(storeToDelete.id);
      toast({
        title: "Store deleted",
        description: `${storeToDelete.name} has been deleted successfully.`,
      });
      setStoreToDelete(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete the store.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteClick = (store: Store) => {
    // Check if user has permission to delete stores
    if (isAdminRole(user?.role)) {
      setStoreToDelete(store);
    } else {
      setShowUnauthorizedDialog(true);
    }
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="overflow-hidden">
            <CardHeader className="pb-2">
              <Skeleton className="h-5 w-1/2 mb-2" />
              <Skeleton className="h-4 w-3/4" />
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/6" />
              </div>
            </CardContent>
            <CardFooter>
              <Skeleton className="h-9 w-full" />
            </CardFooter>
          </Card>
        ))}
      </div>
    );
  }

  if (stores.length === 0) {
    return (
      <Card className="text-center p-6">
        <div className="flex flex-col items-center py-8">
          <h3 className="text-lg font-semibold">No stores found</h3>
          <p className="text-muted-foreground mt-2">
            {isAdminHQ
              ? "Add your first store to get started."
              : "You do not have any stores assigned to you yet."}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {stores.map((store) => (
          <Card
            key={store.id}
            className="overflow-hidden hover:shadow-lg transition-shadow duration-200"
          >
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle className="text-xl">{store.name}</CardTitle>
                <div className="flex gap-2">
                  <Badge variant={store.is_active ? "default" : "outline"}>
                    {store.is_active ? "Active" : "Inactive"}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <Badge
                  variant="outline"
                  className={`flex items-center gap-1 ${getIndustryColor(
                    store.industry_type || "other"
                  )}`}
                >
                  {GetIndustryIcon(store.industry_type || "other")}
                  {formatIndustryType(store.industry_type || "other")}
                </Badge>
              </div>
              <CardDescription>ID: {store.id}</CardDescription>
            </CardHeader>

            <CardContent>
              <div className="space-y-1 text-sm">
                <p>{store.address}</p>
                <p>{store.phone}</p>
                <p className="text-muted-foreground">{store.email}</p>
              </div>
            </CardContent>

            <CardFooter className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => onSelectStore(store.id)}
              >
                <Users className="mr-2 h-4 w-4" />
                Employees
              </Button>

              {isAdminHQ && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setStoreToEdit(store)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteClick(store)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </>
              )}
            </CardFooter>
          </Card>
        ))}
      </div>

      {storeToEdit && (
        <EditStoreDialog
          store={storeToEdit}
          open={!!storeToEdit}
          onOpenChange={(isOpen) => !isOpen && setStoreToEdit(null)}
        />
      )}

      {storeToDelete && (
        <ConfirmDeleteDialog
          open={!!storeToDelete}
          onOpenChange={(isOpen) => !isOpen && setStoreToDelete(null)}
          onConfirm={handleDelete}
          title="Delete Store"
          description={`Are you sure you want to delete ${storeToDelete.name}? This action cannot be undone and will permanently remove all associated data.`}
        />
      )}

      {/* Unauthorized dialog */}
      <Dialog
        open={showUnauthorizedDialog}
        onOpenChange={setShowUnauthorizedDialog}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Permission Denied</DialogTitle>
            <DialogDescription>
              You don't have permission to delete stores. Only Managers and
              Super Admins can perform this action.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setShowUnauthorizedDialog(false)}>
              Understood
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
